import { Play as PlayIcon } from '@tunasong/icons'
import type { Chord, ChordMatch, ChordVariant, NoteEvent } from '@tunasong/music-lib'
import {
  ChordLib,
  NoteLib,
  analyzeChord,
  getChordFunctionAriaLabel,
  isChordVariant,
  nextChord,
} from '@tunasong/music-lib'
import type { ChordBorrowing } from '@tunasong/models'
import type { NoteName, Scale } from '@tunasong/schemas'
import { Chip, Divider, IconButton, Tooltip, Typography } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { GuitarChord } from '../guitar/visualization/guitar-chord.js'
import { usePlayNotes } from '../player/piano-player.hook.js'
import { getChordConfidenceClassName, shouldRenderChordConfidenceLabel } from './chord-confidence.js'
import styles from './chord-info-popup.module.css'

export interface ChordInfoPopupProps {
  chord: Chord
  scale: Scale | null
  onInsertSuggestion: (chord: ChordVariant) => void
  onClose: () => void
}

const NOTE_SEPARATOR = ' – ' // en-dash with regular spaces

const chipColorFor = (borrowing: ChordBorrowing): 'info' | 'default' => {
  // parallel-mode → default (Bowie 2026-05-09): a `warning` tint would imply
  // "songwriting error" for what is actually an expressive choice. The `♭`
  // accidental + description line already convey "borrowed".
  if (borrowing === 'secondary-dominant') {
    return 'info'
  }
  return 'default'
}

export const ChordInfoPopup: FC<ChordInfoPopupProps> = props => {
  const { chord, scale } = props
  const { playNotes } = usePlayNotes()

  const chordName = ChordLib.getName(chord)
  const notes = isChordVariant(chord) ? ChordLib.getNotes(chord) : []
  const prettyNotes = notes.map(n => NoteLib.prettifyNote(n))
  const analysis = isChordVariant(chord) && scale ? analyzeChord({ chord, scale }) : null

  const handlePlay = () => {
    if (!isChordVariant(chord)) {
      return
    }
    const events: NoteEvent[] = ChordLib.getNotes(chord).map(n => ({
      type: 'note',
      note: `${n}4` as NoteName,
      duration: '4n',
    }))
    playNotes(events)
  }

  // Confidence gating: hide the chip and description entirely on `low` (no
  // placeholder), apply 70% opacity on `medium`, render as-is on `high`. The
  // chord NAME above is always shown regardless of confidence (Bowie invariant).
  // Chromatic chords always show their rationale ("Outside the key.") — it's
  // definitive feedback, not a low-confidence label.
  const isChromatic = analysis !== null && analysis.chordFunction === 'chromatic'
  const showRomanChip =
    analysis !== null && !isChromatic && shouldRenderChordConfidenceLabel(analysis.confidence)
  const showRationale = analysis !== null && (isChromatic || shouldRenderChordConfidenceLabel(analysis.confidence))
  const confidenceClassName = analysis ? getChordConfidenceClassName(analysis.confidence) : undefined
  const rationaleClassName = isChromatic ? undefined : confidenceClassName
  const ariaLabel = analysis ? getChordFunctionAriaLabel(analysis.chordFunction, scale) : null

  // Tier 4: up to 2 suggested follow-on chords. ChordLib.equals returns
  // boolean | undefined; treat undefined as "not equal" so we don't drop
  // valid suggestions.
  const suggestions: ChordMatch[] =
    isChordVariant(chord) && scale
      ? nextChord({ current: chord, scale }).filter(m => ChordLib.equals(m.chord, chord) !== true).slice(0, 2)
      : []

  return (
    <div className={styles.card} role="dialog" aria-label={chordName}>
      <div className={styles.identityRow}>
        <h2 className={styles.identityName}>{chordName}</h2>
        {showRomanChip && analysis && ariaLabel ? (
          <Tooltip title={ariaLabel}>
            <Chip
              size="small"
              color={chipColorFor(analysis.borrowing)}
              aria-label={ariaLabel}
              label={analysis.chordFunction}
              tabIndex={-1}
              className={confidenceClassName}
            />
          </Tooltip>
        ) : null}
        <Tooltip title="Play chord">
          <IconButton aria-label="Play chord" onClick={handlePlay} size="small">
            <PlayIcon />
          </IconButton>
        </Tooltip>
      </div>
      {prettyNotes.length > 0 ? (
        <div className={styles.notes} aria-label={`Notes: ${prettyNotes.join(', ')}`}>
          {prettyNotes.join(NOTE_SEPARATOR)}
        </div>
      ) : null}
      {showRationale && analysis ? (
        <div className={cn(styles.rationale, rationaleClassName)}>{analysis.description}</div>
      ) : null}
      <Divider />
      <div className={styles.body}>
        <div className={styles.diagramSlot}>
          <GuitarChord chord={chord} scale={scale ?? undefined} />
        </div>
        {suggestions.length > 0 ? (
          <div className={styles.suggestions}>
            <div className={styles.suggestionsHeading}>Goes well with</div>
            <div className={styles.suggestionsRow}>
              {suggestions.map(match => {
                const suggestionAnalysis = scale ? analyzeChord({ chord: match.chord, scale }) : null
                const showFnLabel =
                  suggestionAnalysis !== null &&
                  suggestionAnalysis.chordFunction !== 'chromatic' &&
                  shouldRenderChordConfidenceLabel(suggestionAnalysis.confidence)
                const fnLabel = showFnLabel && suggestionAnalysis ? suggestionAnalysis.chordFunction : ''
                const fnClassName = suggestionAnalysis
                  ? getChordConfidenceClassName(suggestionAnalysis.confidence)
                  : undefined
                const name = ChordLib.getName(match.chord)
                return (
                  <button
                    key={`${match.chord.root}-${match.chord.variant}`}
                    type="button"
                    className={styles.suggestionChip}
                    aria-label={`Insert ${name}${fnLabel ? ` (${fnLabel})` : ''}`}
                    onClick={() => props.onInsertSuggestion(match.chord)}
                  >
                    <span className={styles.suggestionChipName}>{name}</span>
                    {fnLabel ? (
                      <span className={cn(styles.suggestionChipFunction, fnClassName)}>{fnLabel}</span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
      {scale === null ? (
        <Typography variant="body2" className={styles.footerNote}>
          Set a key for chord analysis
        </Typography>
      ) : null}
    </div>
  )
}

export default ChordInfoPopup
